import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { isEqual } from "lodash";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { useTheme } from "@mui/material/styles";

import {
  getAllEmergencyByWorkspaceId,
  setArchieve,
} from "../../Api/Services/EmergencyServices.js";
import { getPetNamesByWorkspaceId } from "../../Api/Services/PetServices.js";
import "./GlobalItemList.css";
import { ReactComponent as ViewIcon } from "../../Assets/Images/viewIcon.svg";
import useAuth from "../../ContextProvider/AuthProvider";
import { filterAscDescData, formatPhoneNumber, hexToRgba } from "../../Utils.js";
import DetailsModal from "../Common/DetailsModal.js";
import PawItemList from "../Common/PawItemList.js";
import { Button } from "@mui/material";

export default function EmergencyContact() {
  const { petOwnerId: user_id, workspaceId: w_id } = useAuth();
  const [selectedPetName, setSelectedPetName] = useState("");
  const [open, setOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [archiveEmergecyContactId, setArchiveEmergecyContactId] =
    useState(null);
  const [filter, setFilter] = useState({});
  const [pageCount, setPageCount] = useState(0);
  const [emergenctData, setEmergenctData] = useState([]);
  const fetchIdRef = useRef(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const { data: petNames = [] } = useSWR(
    user_id ? `petNames${user_id}` : null,
    async () => {
      const { data } = await getPetNamesByWorkspaceId(w_id);
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const {
    data = [],
    isLoading,
  } = useSWR(
    filter.from > -1 ? `Emergencydata` : null,
    async () => {
      const { data } = await getAllEmergencyByWorkspaceId(w_id);
      const filteredData = data?.data?.map((item) => {
        return {
          ...item,
          id: item.emergency_contact_id,
          full_name: `${item?.first_name} ${item?.last_name}`,
          phone_number: formatPhoneNumber(item?.phone_number),
        };
      });
      return filteredData || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const fetchData = useCallback((oFilter) => {
    const fetchId = ++fetchIdRef.current;
    if (fetchId === fetchIdRef.current && !isEqual(filter, oFilter)) {
      setFilter(oFilter);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { limit, from, sort } = filter;
    if (filter && filter.from > -1) {
      if (data?.length && limit) {
        setPageCount(Math.ceil((data?.length) / limit));
      }
      const allData = [...data];
      const filteredResult = filterAscDescData(allData, sort, from, limit);
      setEmergenctData(filteredResult);
    }
  }, [filter, data]);

  let filteredData;
  if (selectedPetName === "") {
    filteredData = emergenctData;
  } else if (!emergenctData) {
    filteredData = [];
  } else {
    filteredData = emergenctData.filter((item) => {
      const petName = item.name.toLowerCase() || "";
      const query = selectedPetName.toLowerCase();
      return petName.includes(query);
    });
  }

  const petNamesSet = new Set();
  const filteredPetNames = petNames.filter((pet) => {
    const lowercaseName = pet.name.toLowerCase();
    if (!petNamesSet.has(lowercaseName)) {
      petNamesSet.add(lowercaseName);
      return true;
    }
    return false;
  });

  const handleArchiveClick = (emergency_contact_id) => {
    setShowSuccessMessage(true);
    setOpen(true);
    setArchiveEmergecyContactId(emergency_contact_id);
  };
  const handleArchive = () => {
    setIsUpdating(true);

    setArchieve(archiveEmergecyContactId)
      .then((response) => {
        setIsUpdating(false);
        console.log(response);
        if (response.data.success === true) {
          window.location.reload();
        }
      })
      .catch((err) => {
        console.error(err);
        setOpen(true);
        setIsUpdating(false);
      });
  };

  const columns = useMemo(() => {
    return [
      {
        Header: "",
        accessor: "image",
        width: "8vw",
        disableSortBy: true,
        Cell: ({ row }) => {
          return row?.original?.image ? (
            <img
            alt="petImage"
              src={row?.original?.image}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "10px",
                objectFit: "cover",
                margin: "0 auto",
                verticalAlign: "middle",
              }}
            />
          ) : (
            <AccountCircleIcon
              sx={{
                width: "50px",
                height: "50px",
              }}
            />
          );
        },
      },
      {
        Header: "Pet Name",
        accessor: "name",
        width: "12.5vw",
      },
      {
        Header: `Emergency Contact`,
        accessor: "full_name",
        width: "12.5vw",
      },
      {
        Header: "Relationship",
        accessor: "relationship",
        width: "12.5vw",
      },
      {
        Header: "Phone Number",
        accessor: "phone_number",
        width: "12.5vw",
      },
      {
        Header: "",
        accessor: "emergency_contact_id",
        width: "8vw",
        isArchive: true,
        disableSortBy: true,
        disabled: isUpdating,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <UnarchiveIcon
              style={{
                color: "#000000",
              }}
              onClick={() =>
                handleArchiveClick(row?.original?.emergency_contact_id)
              }
            />
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        width: "8vw",
        textAlign: "center",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <Button
              sx={{
                minWidth: "40px",
                background: `${hexToRgba(theme.palette.primary.main, 0.20)} !important`,
                borderRadius: "10px",
                marginRight: "5px",
                width: "24px",
              }}
              onClick={() => navigate(props.viewDataLink(row.original))}
            >
               <ViewIcon
                style={{
                  fill: `${theme.palette.primary.main}`, 
                  cursor: "pointer",
                }}
                alt="view"
              />
            </Button>
          );
        },
      },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultOptions = {
    pageSize: 5,
  };

  const props = {
    label: "Emergency Contacts",
    setSelectedUserName: setSelectedPetName,
    items: filteredPetNames,
    isLoading: isLoading,
    dataAddLink: "/emergency-contact/form",
    columns,
    columnsData: filteredData,
    viewDataLink: (data) =>
      `/emergency-contact/emergency-info/${data.emergency_contact_id}`,
    placeholder: "pet name",
    details: " There are no emergency contact details for this user.",
    fetchData,
    pageCount,
    defaultOptions,
  };

  const ButtonItem = [
    {
      name: "yes",
      onClick: () => {
        handleArchive();
        setShowSuccessMessage(false);
      },
    },
    {
      name: "no",
      onClick: () => {
        setOpen(false);
        setShowSuccessMessage(false);
      },
    },
  ];

  return (
    <>
      <PawItemList {...props} />
      {showSuccessMessage && (
        <>
          <DetailsModal
            open={open}
            severity="warning"
            buttonList={ButtonItem}
            title="Do you want to archive this emergecy contact?"
            handleClose={() => setOpen(false)}
          />
        </>
      )}
    </>
  );
}
